import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { ArrowLeftRight, Download } from "lucide-react";
import { ALL_REASONS, frDateTime, whName, Delta } from "./movementUtils";

const SEL = "rounded-full border border-zinc-800 bg-zinc-900 px-3 py-1.5 text-xs text-zinc-300";

export default function MovementsPanel({ items, warehouses }) {
  const [f, setF] = useState({ item_id: "", reason: "", location_id: "all", start: "", end: "" });
  const [rows, setRows] = useState(null);
  const params = Object.fromEntries(Object.entries(f).filter(([, v]) => v && v !== "all"));
  const key = JSON.stringify(params);
  useEffect(() => { api.get("/stock-movements", { params }).then(({ data }) => setRows(data)).catch(() => setRows([])); }, [key]); // eslint-disable-line
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));
  const exportXlsx = async () => {
    try {
      const { data } = await api.get("/stock-movements-export", { params, responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a"); a.href = url; a.download = `mouvements-stock-${new Date().toISOString().slice(0, 10)}.xlsx`; a.click(); URL.revokeObjectURL(url);
      toast.success("Mouvements exportés");
    } catch { toast.error("Export impossible"); }
  };
  const mats = items.filter((i) => (i.type || "materiel") === "materiel");
  const net = (rows || []).reduce((a, r) => a + r.delta, 0);
  return (
    <div className="space-y-4" data-testid="movements-panel">
      <div className="flex flex-wrap items-center gap-2">
        <ArrowLeftRight className="h-4 w-4 text-[#FF6A00]" />
        <p className="mr-2 text-sm font-semibold text-white">Mouvements de stock</p>
        <select data-testid="mv-filter-item" value={f.item_id} onChange={set("item_id")} className={SEL}>
          <option value="">Tous les articles</option>
          {mats.map((i) => <option key={i.id} value={i.id}>{i.name}</option>)}
        </select>
        <select data-testid="mv-filter-reason" value={f.reason} onChange={set("reason")} className={SEL}>
          <option value="">Tous les motifs</option>
          {Object.entries(ALL_REASONS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
        {warehouses.length > 0 && (
          <select data-testid="mv-filter-wh" value={f.location_id} onChange={set("location_id")} className={SEL}>
            <option value="all">Tous les entrepôts</option>
            {warehouses.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
            <option value="none">Sans entrepôt</option>
          </select>
        )}
        <input data-testid="mv-filter-start" type="date" value={f.start} onChange={set("start")} className={SEL} title="Du" />
        <input data-testid="mv-filter-end" type="date" value={f.end} onChange={set("end")} className={SEL} title="Au" />
        <Button data-testid="mv-export" onClick={exportXlsx} variant="outline" className="ml-auto h-8 rounded-full border-zinc-700 bg-transparent text-xs text-zinc-200 hover:bg-zinc-800"><Download className="mr-1 h-3.5 w-3.5" />Excel</Button>
      </div>
      {rows && <p className="text-xs text-zinc-500" data-testid="mv-summary">{rows.length >= 5000 ? "5000 derniers mouvements (affinez les filtres)" : `${rows.length} mouvement(s)`} · variation nette <Delta d={net} /></p>}
      {rows && rows.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-zinc-800 bg-zinc-900/40 py-12 text-center text-sm text-zinc-500">Aucun mouvement pour ces filtres.</div>
      ) : rows && (
        <div className="overflow-x-auto rounded-2xl border border-zinc-800 bg-zinc-900">
          <table className="w-full min-w-[720px] text-sm">
            <thead><tr className="border-b border-zinc-800 text-left text-xs text-zinc-500">
              {["Date", "Article", "Entrepôt", "Avant → Après", "Variation", "Motif", "Par"].map((h) => <th key={h} className="px-4 py-2.5 font-medium">{h}</th>)}
            </tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} data-testid={`mv-row-${r.id}`} className="border-b border-zinc-800/60 last:border-0">
                  <td className="whitespace-nowrap px-4 py-2.5 text-zinc-400">{frDateTime(r.at)}</td>
                  <td className="px-4 py-2.5 text-white">{r.item_name}</td>
                  <td className="px-4 py-2.5 text-zinc-400">{whName(warehouses, r.location_id)}</td>
                  <td className="px-4 py-2.5 text-zinc-300">{r.before} → {r.after}</td>
                  <td className="px-4 py-2.5"><Delta d={r.delta} /></td>
                  <td className="px-4 py-2.5 text-zinc-300">{ALL_REASONS[r.reason] || "Autre"}{r.note && <span className="block text-xs text-zinc-500">{r.note}</span>}</td>
                  <td className="px-4 py-2.5 text-zinc-400">{r.by}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
